/* eslint-disable complexity */
import {
  first_value,
  read_safe_object_value,
  write_safe_object_value
} from '@/services/onboarding_form_helpers';
import {
  get_visible_questions,
  normalize_answer_group_key,
  resolve_section_question_id
} from './queue_form_saved_answer_helpers';
export { load_selected_application_form_data } from './queue_form_load_helpers';
export {
  get_visible_questions,
  normalize_answer_group_key,
  prefill_question_value,
  resolve_section_question_id
} from './queue_form_saved_answer_helpers';
export {
  create_blank_runtime_sub_section_instance,
  create_runtime_sub_section_instance,
  materialize_repeatable_steps
} from './queue_form_runtime_helpers';

export const parse_application_forms = (application_forms) => application_forms
  .map((form) => ({
    ...form,
    id: String(first_value([form?.application_form_id, form?.id], '')),
    application_form_id: Number(first_value([form?.application_form_id, form?.id], 0)) || null,
    form_id: Number(first_value([form?.form?.id, form?.form_id], 0)) || null,
    entity_profile_id: Number(first_value([
      form?.entity_profile_id,
      form?.entity_profile,
      form?.entity?.id,
      form?.entity_profile?.id
    ], 0)) || null,
    entity_type_id: Number(first_value([
      form?.entity_type_id,
      form?.entity_type?.id,
      form?.entity_type?.entity_type_id,
      form?.threshold_entity_type_id_used
    ], 0)) || null,
    entity_type_name: first_value([
      form?.entity_type_name,
      form?.entity_type?.name,
      form?.entity_type?.entity_type_name,
      form?.journey
    ], ''),
    journey: first_value([
      form?.journey,
      form?.entity_type_name,
      form?.entity_type?.name,
      form?.entity_type?.entity_type_name
    ], ''),
    form_name: first_value([form?.form?.name, form?.form?.form_name, form?.name, form?.title], 'Onboarding form')
  }))
  .filter((form) => form.id && form.form_id);

export const select_requested_application_form = (application_forms, requested_application_form_id = '') => (
  application_forms.find((form) => form.id === requested_application_form_id)?.id
  || application_forms[0]?.id
  || ''
);

const collect_question_results = (intake_steps, collector) => {
  const results = [];

  intake_steps.forEach((main_section) => {
    main_section.sub_sections.forEach((sub_section) => {
      collector(sub_section, results);
    });
  });

  return results;
};

export const build_standard_answers = (
  intake_steps,
  uses_file,
  normalize_answer_value,
  form_values,
  should_skip_question = () => false
) => (
  collect_question_results(intake_steps, (sub_section, answers) => {
    get_visible_questions(sub_section.questions || [], form_values).forEach((question) => {
      if (!question.id || uses_file(question) || should_skip_question(question)) return;

      const normalized_value = normalize_answer_value(question, read_safe_object_value(form_values, question.id, ''));
      if (!normalized_value) return;

      const answer_payload = {
        section_question_id: resolve_section_question_id(question),
        answer_text: normalized_value
      };
      if (question.answer_group_key) answer_payload.answer_group_key = question.answer_group_key;

      answers.push(answer_payload);
    });
  })
);

export const build_file_answer_payload = (
  intake_steps,
  uses_file,
  file_values,
  form_values = {},
  should_skip_question = () => false
) => {
  const files = {};
  const answers = collect_question_results(intake_steps, (sub_section, collected_answers) => {
    get_visible_questions(sub_section.questions || [], form_values).forEach((question) => {
      if (!question.id || !uses_file(question) || should_skip_question(question)) return;

      const raw_files = read_safe_object_value(file_values, question.id, []);
      const question_files = Array.isArray(raw_files) ? raw_files : raw_files ? [raw_files] : [];
      const upload_files = question_files.filter((file) => file && !file.persisted && !file.removed);
      const removed_answer_ids = question_files
        .filter((file) => file?.persisted && file?.removed && file?.id)
        .map((file) => file.id);

      if (!upload_files.length && !removed_answer_ids.length) return;

      const answer_payload = {
        section_question_id: resolve_section_question_id(question),
        removed_answer_ids
      };
      if (question.answer_group_key) answer_payload.answer_group_key = question.answer_group_key;

      collected_answers.push(answer_payload);
      write_safe_object_value(files, `file_${resolve_section_question_id(question)}`, upload_files);
    });
  });
  const payload = { answers, files };

  return payload;
};

export const build_repeatable_groups = (intake_steps = []) => {
  const groups = [];

  intake_steps.forEach((main_section) => {
    const instances_by_base_section_id = new Map();
    (Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : []).forEach((sub_section) => {
      const base_section_id = String(first_value([sub_section?.base_section_id, sub_section?.id], '')).trim();
      if (!base_section_id) return;

      const existing_instances = instances_by_base_section_id.get(base_section_id) || [];
      existing_instances.push(sub_section);
      instances_by_base_section_id.set(base_section_id, existing_instances);
    });

    instances_by_base_section_id.forEach((instances) => {
      const active_keys = instances
        .map((sub_section) => normalize_answer_group_key(sub_section.answer_group_key))
        .filter(Boolean);
      if (!active_keys.length) return;

      groups.push({
        section_question_ids: [
          ...new Set(
            (instances[0]?.questions || [])
              .map((question) => resolve_section_question_id(question))
              .filter(Boolean)
          )
        ],
        active_keys
      });
    });
  });

  return groups;
};

const FILE_SECTION_PRIORITY_MATCHERS = ['document', 'upload', 'attachment', 'file', 'evidence', 'certification'];

export const step_file_priority = (step, uses_file, normalize_text) => {
  const section_label = normalize_text(step?.label);
  const sub_sections = Array.isArray(step?.sub_sections) ? step.sub_sections : [];
  const has_file_question = sub_sections.some((sub_section) => (
    Array.isArray(sub_section?.questions)
      && sub_section.questions.some((question) => uses_file(question))
  ));
  const label_matches_file_section = FILE_SECTION_PRIORITY_MATCHERS.some((matcher) => section_label.includes(matcher));

  if (has_file_question && label_matches_file_section) return 3;
  if (has_file_question) return 2;
  if (label_matches_file_section) return 1;
  return 0;
};

export const prioritize_file_sections = (steps, uses_file, normalize_text) => [...steps]
  .sort((left_step, right_step) => {
    const left_priority = step_file_priority(left_step, uses_file, normalize_text);
    const right_priority = step_file_priority(right_step, uses_file, normalize_text);

    if (left_priority !== right_priority) {
      return right_priority - left_priority;
    }

    return (left_step?.number || 0) - (right_step?.number || 0);
  })
  .map((step, index) => ({
    ...step,
    number: index + 1
  }));

export const question_input_type = (question = {}) => {
  if (['number', 'integer', 'decimal', 'float'].includes(question.type)) return 'number';
  if (question.type === 'email') return 'email';
  if (question.type === 'date') return 'date';
  if (['phone', 'telephone'].includes(question.type)) return 'tel';
  return 'text';
};

export const uses_textarea = (question = {}) => ['long_text', 'textarea', 'paragraph'].includes(question.type);
export const uses_select = (question = {}) => ['single_select', 'dropdown', 'select'].includes(question.type) && question.options.length > 0;
export const uses_radio = (question = {}) => ['radio', 'yes_no', 'boolean'].includes(question.type) && question.options.length > 0;
export const uses_checkbox_group = (question = {}) => question.type === 'multi_select' && question.options.length > 0;
export const uses_file = (question = {}) => ['file', 'file_upload', 'attachment'].includes(question.type);
export const uses_facial_verification = (question = {}) => question.type === 'facial_verification';
export const uses_checkbox = (question = {}) => ['checkbox', 'toggle'].includes(question.type);

export const normalize_answer_value = (question, value, uses_checkbox_group_fn, uses_checkbox_fn) => {
  if (value === undefined || value === null || value === '') return '';
  if (uses_checkbox_group_fn(question)) return normalize_multi_select_value(value);
  if (uses_checkbox_fn(question) || typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value).trim();
};

const normalize_multi_select_value = (value) => Array.isArray(value) ? value.join(', ') : '';

export const create_selected_form_state = (selected_form) => ({
  selected_application_form_meta: selected_form,
  selected_application_form_detail: null,
  allowed_actions: [],
  intake_steps: [],
  current_step: 1,
  form_values: {},
  file_values: {},
  persisted_file_urls: {}
});

export const is_question_answered = (question, form_values, file_values, uses_file_fn, uses_checkbox_group_fn, read_safe_value) => {
  if (!question?.id) return true;
  if (uses_file_fn(question)) {
    const files = read_safe_value(file_values, question.id, []);
    return (Array.isArray(files) ? files : files ? [files] : []).some((file) => file && !file.removed);
  }

  const value = read_safe_value(form_values, question.id, '');
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return value;
  return String(value ?? '').trim() !== '';
};
